import { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { StorageRoom } from '../../services/storageRoom.service';
import { storageRoomService } from '../../services/storageRoom.service';
import {
  formatEntityDisplayName,
  resolveSampleNameForSubmit,
} from '../../utils/softBreakIdentifier';
import { AppModalCloseButton } from '../common/AppModalCloseButton';
import TextContentSep from '../common/TextContentSep';
import { joinLabeledValue } from '../../utils/joinLabeledValue';

const storageRoomSchema = (t: (key: string) => string) => z.object({
  name: z.string().min(1, t('storage_room.name_required')).max(255, t('storage_room.name_max_length')),
  location: z.string().max(255).nullable().optional(),
  type: z.string().max(100).nullable().optional(),
  isShared: z.boolean().optional(),
});

type StorageRoomFormData = z.infer<ReturnType<typeof storageRoomSchema>>;

interface StorageRoomFormModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (data: {
    name?: string;
    location?: string | null;
    type?: string | null;
    isShared?: boolean;
  }) => Promise<void>;
  initialData?: StorageRoom | null;
  isLoading?: boolean;
}

export default function StorageRoomFormModal({
  show,
  onHide,
  onSubmit,
  initialData,
  isLoading = false,
}: StorageRoomFormModalProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string>('');
  const [showShareConfirm, setShowShareConfirm] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('');
  const [generatedName, setGeneratedName] = useState<string>('');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<StorageRoomFormData>({
    resolver: zodResolver(storageRoomSchema(t)),
    defaultValues: {
      name: '',
      location: null,
      type: null,
      isShared: false,
    },
  });

  const isShared = watch('isShared') ?? false;
  const watchedType = watch('type');
  const currentType = show ? (watchedType ?? getValues('type')) : undefined;

  useEffect(() => {
    if (!show) {
      // モーダルが閉じたときは、すべての状態をリセット
      reset({
        name: '',
        location: null,
        type: null,
        isShared: false,
      });
      setSelectedType('');
      setGeneratedName('');
      setError('');
      setShowShareConfirm(false);
      return;
    }

    // モーダルが開いたときのみ、フォームを初期化
    if (initialData) {
      reset({
        // 一覧と同じ表示名。送信時は resolveSampleNameForSubmit で生名を復元
        name: formatEntityDisplayName(initialData.name || '', t),
        location: initialData.location || null,
        type: initialData.type || null,
        isShared: initialData.isShared ?? false,
      });
      setSelectedType(initialData.type || '');
    } else {
      // 新規作成時は、明示的に undefined を設定して watch() をリセット
      reset({
        name: '',
        location: null,
        type: null,
        isShared: false,
      });
      setValue('name', '');
      setValue('location', null);
      setValue('type', null);
      setSelectedType('');
      setGeneratedName('');
    }
    setError('');
  }, [initialData, show, reset, setValue, t]);

  // タイプが変更されたときに名前を自動生成（編集時は既存の名前をクリアしない）
  useEffect(() => {
    // モーダルが閉じている場合は何もしない
    if (!show) {
      return;
    }

    const generateName = async () => {
      const type = watchedType ?? getValues('type');
      if (!initialData && type) {
        try {
          const response = await storageRoomService.generateName(type);
          setGeneratedName(response.data.name);
          setValue('name', response.data.name);
        } catch (err) {
          console.error('名前自動生成エラー:', err);
          setGeneratedName('');
          setValue('name', '');
        }
      } else if (!watchedType && !initialData) {
        // 新規時のみ：タイプが未選択の場合は名前をクリア
        setGeneratedName('');
        setValue('name', '');
      }
    };

    const type = watchedType ?? getValues('type');

    if (type) {
      generateName();
    } else {
      setGeneratedName('');
      // 編集時は既存の保管庫名をクリアしない（reset の反映前に effect が走る場合の対策）
      if (!initialData) {
        setValue('name', '');
      }
    }
  }, [watchedType, initialData, setValue, show, getValues]);

  const handleSharedToggle = (next: boolean) => {
    const current = watch('isShared') ?? false;
    if (next && !current) {
      setShowShareConfirm(true);
      return;
    }
    setValue('isShared', next);
  };

  const confirmEnableSharing = () => {
    setValue('isShared', true);
    setShowShareConfirm(false);
  };

  const onSubmitForm = async (data: StorageRoomFormData) => {
    try {
      setError('');
      await onSubmit({
        name: resolveSampleNameForSubmit(
          data.name!,
          initialData?.name,
          t
        ),
        location: data.location || null,
        type: data.type || null,
        isShared: data.isShared ?? false,
      });
    } catch (err: any) {
      // バックエンドからのエラーメッセージを取得
      const errorMessage = err.response?.data?.error?.message || err.message;
      // 翻訳キーの場合は翻訳、そうでない場合はそのまま表示
      if (errorMessage && (errorMessage.startsWith('storage_room.') || errorMessage.startsWith('common.') || errorMessage.startsWith('api.error.'))) {
        setError(t(errorMessage, { defaultValue: errorMessage }));
      } else {
        setError(errorMessage || t('common.save_failed', { defaultValue: '保存に失敗しました' }));
      }
    }
  };

  const storageTypes = [
    t('storage_room.storage_types.ultra_low_temp_freezer'),
    t('storage_room.storage_types.refrigerator'),
    t('storage_room.storage_types.room_temp_storage'),
    t('storage_room.storage_types.dry_storage'),
    t('storage_room.storage_types.liquid_nitrogen_tank'),
    t('storage_room.storage_types.other'),
  ];

  return (
    <Modal dialogClassName="sample-system-dialog" show={show} onHide={onHide} size="lg">
      <Modal.Header>
        <Modal.Title>
          {initialData ? t('storage_room.modal_edit_title') : t('storage_room.modal_create_title')}
        </Modal.Title>
        <AppModalCloseButton
          onClick={onHide}
          ariaLabel={t('common.close')}
          title={
            initialData
              ? t('storage_room.modal_edit_close_title', {
                  defaultValue: '保管庫の編集を閉じる',
                })
              : t('storage_room.modal_create_close_title', {
                  defaultValue: '保管庫の登録を閉じる',
                })
          }
        />
      </Modal.Header>
      <Form onSubmit={handleSubmit(onSubmitForm)}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* 保管庫設置場所（タイプ・保管庫名の前） */}
          <Form.Group className="mb-3">
            <Form.Label
              htmlFor="storage-room-location"
              title={t('storage_room.location', { defaultValue: '設置場所' })}
            >
              {t('storage_room.location', { defaultValue: '設置場所' })}
            </Form.Label>
            <TextContentSep />
            <Form.Control
              id="storage-room-location"
              name="location"
              type="text"
              {...register('location')}
              placeholder={t('storage_room.location_placeholder', { defaultValue: '例 · 1階 実験室A' })}
              title={t('storage_room.location_hint', {
                defaultValue: '設置場所を入力 · 任意',
              })}
            />
            <Form.Text className="text-muted">
              {t('storage_room.location_hint', {
                defaultValue: '設置場所を入力 · 任意',
              })}
            </Form.Text>
            <TextContentSep />
          </Form.Group>

          {/* タイプ */}
          <Form.Group className="mb-3">
            <Form.Label
              htmlFor="storage-room-type"
              title={t('storage_room.field_type_label')}
            >
              {t('storage_room.field_type_label')} <span className="text-danger">*</span>
            </Form.Label>
            <TextContentSep />
            <Form.Select
              id="storage-room-type"
              name="type"
              {...register('type')}
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setValue('type', e.target.value || null);
              }}
              disabled={!show || !!initialData}
            >
              <option value="">{t('storage_room.select_type_placeholder')}</option>
              {storageTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
            <TextContentSep />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label
              htmlFor="storage-room-name"
              title={t('storage_room.field_storage_name_label')}
            >
              {t('storage_room.field_storage_name_label')}{' '}
              <span className="text-danger">*</span>
            </Form.Label>
            <TextContentSep />
            <Form.Control
              id="storage-room-name"
              name="name"
              type="text"
              {...register('name')}
              isInvalid={!!errors.name}
              placeholder={!initialData && generatedName ? generatedName : t('storage_room.storage_name_placeholder', { defaultValue: '例 · 保管庫001' })}
              readOnly={!initialData && !!generatedName}
              disabled={!show || (!currentType && !initialData)}
            />
            {/* 未選択時に name_hint と select_type_first が連続して潰れるのを防ぐ（排他表示） */}
            {!initialData && !currentType && show ? (
              <Form.Text className="text-muted d-block">
                {t('storage_room.select_type_first_message')}
              </Form.Text>
            ) : null}
            {!initialData && currentType && generatedName ? (
              <Form.Text className="text-muted d-block">
                {joinLabeledValue(t('storage_room.auto_generated_name'), generatedName)}
              </Form.Text>
            ) : null}
            {!initialData && currentType && !generatedName ? (
              <Form.Text className="text-muted d-block">
                {t('storage_room.name_hint', { defaultValue: '保管庫名を入力してください' })}
              </Form.Text>
            ) : null}
            {errors.name && (
              <Form.Control.Feedback type="invalid">
                {errors.name.message}
              </Form.Control.Feedback>
            )}
            <TextContentSep />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="storage-room-is-shared"
              label={t('storage_room.is_shared_label_short', {
                defaultValue: t('storage_room.is_shared_label'),
              })}
              title={t('storage_room.is_shared_label')}
              checked={isShared}
              onChange={(e) => handleSharedToggle(e.target.checked)}
            />
            <TextContentSep />
            <Form.Text className="text-muted" title={t('storage_room.is_shared_label')}>
              {t('storage_room.is_shared_hint')}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
            disabled={isLoading}
            aria-label={t('common.cancel')}
            title={t('common.cancel')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isLoading}
            aria-label={
              isLoading
                ? t('common.saving')
                : initialData
                  ? t('common.update')
                  : t('common.create')
            }
            title={
              isLoading
                ? t('common.saving')
                : initialData
                  ? t('storage_room.submit_update_title', {
                      defaultValue: '保管庫を更新する',
                    })
                  : t('storage_room.submit_create_title', {
                      defaultValue: '保管庫を作成する',
                    })
            }
          >
            {isLoading ? t('common.saving') : initialData ? t('common.update') : t('common.create')}
          </Button>
        </Modal.Footer>
      </Form>

      <Modal
        dialogClassName="sample-system-dialog"
        show={showShareConfirm}
        onHide={() => setShowShareConfirm(false)}
        centered
      >
        <Modal.Header>
          <Modal.Title>{t('storage_room.is_shared_confirm_title')}</Modal.Title>
          <AppModalCloseButton
            onClick={() => setShowShareConfirm(false)}
            ariaLabel={t('common.close')}
            title={t('storage_room.is_shared_confirm_close_title', {
              defaultValue: '共有確認を閉じる',
            })}
          />
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-0">
            {t('storage_room.is_shared_confirm_body')}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowShareConfirm(false)}
            aria-label={t('common.cancel')}
            title={t('common.cancel')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="warning"
            onClick={confirmEnableSharing}
            aria-label={t('storage_room.is_shared_confirm_action')}
            title={t('storage_room.is_shared_confirm_title')}
          >
            {t('storage_room.is_shared_confirm_action')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
}
